import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  DatePicker,
  Select,
  notification,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import PageContentHeader from "../../../components/common/PageContentHeader";
import Title from "antd/es/skeleton/Title";
import InputSelect from "../../../components/common/InputSelect";
import {
  getVehicleModelService,
  getVehicleVarientService,
  getVehicleColorService,
  getVehicleFuelTypeService,
  getVehicleSeaterService,
  getAreasInStockService,
} from "../../../services/master-services";
import {
  addChassisNumberService,
  getVinDetailsByIdService,
  updateVinService,
} from "../../../services/stock-services";
import Loader from "../../../components/common/Loader";
import {
  API_RESPONSE_CODES,
  STOCK_CURRENT_STATUS_NAME,
} from "../../../utills/constant";
import { ArrowLeftOutlined } from "@ant-design/icons";

const masterOptions = {
  stockStatus: ["Physical", "Transit", "MDDP"],
};

// Blocked Reason Select Component
const BlockedReasonSelectWrapper = ({
  form,
  isFieldEnabled,
  value,
  onChange,
}) => {
  const blockedStatus = Form.useWatch("blocked_status", form);
  return (
    <Select
      className="form-control-ant"
      placeholder="Select Blocked Reason"
      disabled={!isFieldEnabled("blocked_reason", blockedStatus)}
      value={value || undefined}
      onChange={(val) => {
        onChange?.(val);
        // Trigger validation after value changes
        setTimeout(() => {
          form.validateFields(["blocked_reason"]);
        }, 100);
      }}
    >
      <Select.Option value="Management">Management</Select.Option>
      <Select.Option value="OEM">OEM</Select.Option>
      <Select.Option value="VIP">VIP</Select.Option>
      <Select.Option value="Other">Other</Select.Option>
    </Select>
  );
};

const AddEditVinForm = () => {
  const navigate = useNavigate();
  const { id, vin } = useParams();
  const [form] = Form.useForm();
  const stockStatus = Form.useWatch("stock_status", form);
  const [isLoading, setIsLoading] = useState(false);

  const sanitizeChassisInput = (value) =>
    value ? value.replace(/[^A-Za-z0-9]/g, "") : value;

  const blockInvalidChassisKeys = (e) => {
    if (e.key.length > 1) return;
    if (!/^[A-Za-z0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  // Helper function to block invalid number keys and limit decimal places
  const blockInvalidNumberKeys = (e) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Helper function to normalize value to max 2 decimal places
  const normalizeDecimalInput = (value) => {
    if (!value && value !== 0) return value;
    const str = value.toString().replace(/[eE+-]/g, "");
    const parts = str.split(".");
    if (parts[1] && parts[1].length > 2) {
      return parseFloat(str).toFixed(2);
    }
    return str;
  };
  const [modalData, setModalData] = useState([]);
  const [variantData, setVariantData] = useState([]);
  const [colorData, setColorData] = useState([]);
  const [fuelData, setFuelData] = useState([]);
  const [seatData, setSeatData] = useState([]);
  const [areaData, setAreaData] = useState([]);
  const isEditMode = !!vin; // If vin exists, it's edit mode
  const isChassisEditable = !isEditMode || stockStatus === "MDDP";
  const [hasLeadId, setHasLeadId] = useState(false); // Track if lead_id exists
  const [currentStatus, setCurrentStatus] = useState(null); // Store current_status from API
  const [vehicleIds, setVehicleIds] = useState({
    make_id: 1, // Toyota make_id
    model_id: "",
    variant_id: "",
  });

  useEffect(() => {
    getModelData(vehicleIds.make_id);
    getAreaData();
    if (isEditMode) {
      fetchVinDetailsById();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVinDetailsById = async () => {
    try {
      setIsLoading(true);
      // Use ID directly from URL params
      if (id) {
        // Call the API directly with the ID
        const response = await getVinDetailsByIdService(id);
        console.log("VIN Details Response:", response);

        const data = response?.data?.data;
        if (data) {
          // Store current_status from API response
          setCurrentStatus(data.current_status || null);

          // Check if lead_id exists and set state
          setHasLeadId(!!data.lead_id);

          // Update vehicle IDs state
          setVehicleIds({
            make_id: 1,
            model_id: data.vehicle_model_id,
            variant_id: data.vehicle_variant_id,
          });

          // Load dependent data
          if (data.vehicle_model_id) {
            await getVariantData(data.vehicle_model_id);
          }
          if (data.vehicle_model_id && data.vehicle_variant_id) {
            await Promise.all([
              getColorData(1, data.vehicle_model_id, data.vehicle_variant_id),
              getFuelData(1, data.vehicle_model_id, data.vehicle_variant_id),
              getSeatData(1, data.vehicle_model_id, data.vehicle_variant_id),
            ]);
          }

          // Get stock_status from the response or use default
          const stockStatus = data.stock_status;

          // Prepare form values
          const formValues = {
            vin: data.chassis_number,
            stock_status: stockStatus,
            vehicle_model_id: data.vehicle_model_id,
            vehicle_variant_id: data.vehicle_variant_id,
            color_id: data.color_id,
            fuel_type_id: data.fuel_type_id,
            seater_id: data.seater_id,
            tkmPrice: data.tkm_price || 0,
            area_id: data.physical_location_id,
            wddpDate: data.wddp_date ? dayjs(data.wddp_date) : null,
            oem_invoice_date: data.oem_invoice_date
              ? dayjs(data.oem_invoice_date)
              : null,
          };

          // Add edit mode fields
          if (isEditMode) {
            Object.assign(formValues, {
              delivery_date: data.delivery_date
                ? dayjs(data.delivery_date)
                : null,
              rto_date: data.rto_date ? dayjs(data.rto_date) : null,
              mddp_month:
                data.mddp_month && data.mddp_month !== ""
                  ? dayjs(data.mddp_month)
                  : null,
              age_days_in_stock: data.stock_age || "",
              allocation_status: data.allocation_status || "",
              allocation_date: data.allocation_date
                ? dayjs(data.allocation_date)
                : null,
              allocation_age: data.allocation_age || "",
              blocked_status: data.blocked_status || "No",
              blocked_reason: data.blocked_reason || "",
              blocked_date: data.blocked_date ? dayjs(data.blocked_date) : null,
              blocking_age: data.blocking_age || "",
              co_dealer_purchase: data.co_dealer_purchase || "No",
              delivery_status:
                data.delivery_status &&
                  data.delivery_status !== "" &&
                  data.delivery_status !== "NA"
                  ? data.delivery_status
                  : undefined,
              delivery_date_workflow: data.delivery_date
                ? dayjs(data.delivery_date)
                : null,
              delivery_denied_flag: data.delivery_denied || "No",
              customer_name: data.customer_name || "",
              contact_no: data.customer_phone || "",
              area_of_booking: data.customer_area || "",
              sales_officer_name: data.sales_officer_name || "",
              team_lead_name: data.team_leader_name || "",
              date_of_booking: data.booking_date
                ? dayjs(data.booking_date)
                : null,
              order_age: data.order_age || "",
              lead_status: data.lead_status || "",
              ctdms_order_no: data.ctdms_order_no || "",
              advanced_billing_name: data.advanced_billing_name || "",
              retail_status: data.retail_status || "",
              retail_date: data.retail_date ? dayjs(data.retail_date) : null,
              retail_month:
                data.retail_month && data.retail_month !== ""
                  ? dayjs(data.retail_month, "MM-YYYY")
                  : null,
              ex_showroom_price: data.ex_showroom_price || 0,
              registration_charges: data.registration_charges || 0,
              road_tax: data.road_tax || 0,
              rto_cost: data.rto_cost || 0,
              insurance_amount: data.insurance_amount || 0,
              insurance_company: data.insurance_company || null,
              handling_charges: data.handling_charges || 0,
              extended_warranty: data.extended_warranty || 0,
              amc_amount: data.amc_amount || 0,
              accessories_dlc_amt: data.accessories_amount || 0,
              protection_3m_amount: data["3m_protection_amount"] || 0,
              fastag: data.fastag || 0,
              tcs: data.tcs || 0,
              choice_number_charges: data.choice_number_charges || 0,
              other_logistic_charges: data.other_logistic_charges || 0,
              additional_discount: data.additional_discount || 0,
              other_oem_offer: data.other_oem_offer || 0,
              consumer_offer: data.consumer_offer || 0,
              discount_approval_insurance:
                data.discount_approval_insurance || 0,
              exchange_offer: data.exchange_offer || 0,
              corporate_offer: data.corporate_offer || 0,
              other_dealer_offer: data.other_dealer_offer || 0,
              on_road_value: data.on_road_price || 0,
              payment_mode: data.payment_mode || "",
              financier_name:
                data.financier_id ||
                (data.financier_name && data.financier_name !== ""
                  ? null
                  : null),
              in_house_finance:
                data.in_house && data.in_house !== "" ? data.in_house : "No",
              file_stage:
                data.file_stage_id ||
                (data.file_stage && data.file_stage !== "" ? null : null),
              total_booking_amount: data.total_booking_amount || 0,
              total_down_payment_amount: data.total_down_payment_amount || 0,
              total_do_amount: data.total_delivery_order_amount || 0,
              total_disbursement_amount: data.total_disbursement_amount || 0,
              total_refund_amount: data.total_refund_amount || 0,
              total_payment_received: data.total_payment_received || 0,
              net_customer_balance: data.net_customer_balance || 0,
              document_collection_request_status:
                data.document_collection_request_status &&
                  data.document_collection_request_status !== "" &&
                  data.document_collection_request_status !== "NA"
                  ? data.document_collection_request_status
                  : undefined,
              delivery_approval_branch_manager:
                data.delivery_approval_sales_manager_request_status &&
                  data.delivery_approval_sales_manager_request_status !== "" &&
                  data.delivery_approval_sales_manager_request_status !== "NA"
                  ? data.delivery_approval_sales_manager_request_status
                  : undefined,
              delivery_approval_accounts:
                data.delivery_approval_head_office_manager_request_status &&
                  data.delivery_approval_head_office_manager_request_status !==
                  "" &&
                  data.delivery_approval_head_office_manager_request_status !==
                  "NA"
                  ? data.delivery_approval_head_office_manager_request_status
                  : undefined,
              delivery_request_status:
                data.delivery_request_status &&
                  data.delivery_request_status !== "" &&
                  data.delivery_request_status !== "NA"
                  ? data.delivery_request_status
                  : undefined,
              cancellation_request_status:
                data.cancellation_request_status &&
                  data.cancellation_request_status !== "" &&
                  data.cancellation_request_status !== "NA"
                  ? data.cancellation_request_status
                  : undefined,
              refund_processed: data.refund_processed || "No",
              gate_pass_generated: data.gate_pass_generated || "",
              delivery_confirmation_documents:
                data.delivery_confirmation_documents || "",
            });
          }

          // Set form values
          form.setFieldsValue(formValues);
        }
      } else {
        console.log("VIN ID not found in URL params");
        notification.error({
          description: "VIN ID is required to fetch details",
        });
      }
    } catch (error) {
      console.error("Error fetching VIN details:", error);
      notification.error({
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Failed to fetch VIN details",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getModelData = async (make_id) => {
    setIsLoading(true);
    try {
      const modelDataArray = await getVehicleModelService(make_id, true);
      setModalData(modelDataArray?.data?.data);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getVariantData = async (model_id) => {
    setIsLoading(true);
    try {
      const variantDataArray = await getVehicleVarientService(model_id, true);
      setVariantData(variantDataArray?.data?.data);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getColorData = async (make_id, model_id, variant_id) => {
    setIsLoading(true);
    try {
      const colorDataArray = await getVehicleColorService(
        make_id,
        model_id,
        variant_id,
        true
      );
      setColorData(colorDataArray?.data?.data);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getSeatData = async (make_id, model_id, variant_id) => {
    setIsLoading(true);
    try {
      const seatDataArray = await getVehicleSeaterService(
        make_id,
        model_id,
        variant_id,
        true
      );
      setSeatData(seatDataArray?.data?.data);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFuelData = async (make_id, model_id, variant_id) => {
    setIsLoading(true);
    try {
      const fuelDataArray = await getVehicleFuelTypeService(
        make_id,
        model_id,
        variant_id,
        null,
        true
      );
      setFuelData(fuelDataArray?.data?.data);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getAreaData = async () => {
    setIsLoading(true);
    try {
      const areaDataArray = await getAreasInStockService(true);
      setAreaData(areaDataArray?.data?.data);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to check if a field should be enabled based on current_status
  const isFieldEnabled = (fieldName, additionalCondition = null) => {
    // If current_status is null, return false (default disabled state)
    if (!currentStatus) {
      return false;
    }

    // Normalize currentStatus - handle both key ('DELIVERED') and display value ('Delivered')
    const normalizedStatus = Object.values(STOCK_CURRENT_STATUS_NAME).includes(
      currentStatus
    )
      ? currentStatus
      : STOCK_CURRENT_STATUS_NAME[currentStatus] || currentStatus;

    // Special handling for blocked_reason: enabled when blocked_status is 'Yes' AND current_status is not 'Delivered'
    if (fieldName === "blocked_reason") {
      // If blocked_status is not provided, return false (disabled)
      if (
        additionalCondition === null ||
        additionalCondition === undefined ||
        additionalCondition === ""
      ) {
        return false;
      }
      const blockedStatus = String(additionalCondition).trim();
      // Enabled only when: blocked_status is 'Yes' AND current_status is not 'Delivered'
      return (
        blockedStatus === "Yes" &&
        normalizedStatus !== STOCK_CURRENT_STATUS_NAME.DELIVERED
      );
    }

    // Map field names to their editable status for each stock status
    const fieldEditableMap = {
      // Model, Variant, Color, Fuel Type, Seater: Only editable for NOT_ALLOCATED
      vehicle_model_id:
        normalizedStatus === STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
      vehicle_variant_id:
        normalizedStatus === STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
      color_id: normalizedStatus === STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
      fuel_type_id:
        normalizedStatus === STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
      seater_id: normalizedStatus === STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,

      // Stock Status, Branch/Location, TKM Price, WDDP Date, MDDP Month: Editable for NOT_ALLOCATED, REQUESTED, ALLOCATED, RETAILED
      stock_status: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      area_id: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      tkmPrice: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      wddpDate: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      mddp_month: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      oem_invoice_date: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),

      // Blocked Status, Blocked Reason: Editable for NOT_ALLOCATED, REQUESTED, ALLOCATED, RETAILED
      blocked_status: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      blocked_reason: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),

      // Co-dealer Purchase: Editable for NOT_ALLOCATED, REQUESTED, ALLOCATED, RETAILED
      co_dealer_purchase: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),

      // Contact No: Editable for NOT_ALLOCATED, REQUESTED, ALLOCATED, RETAILED
      contact_no: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),

      // CTDMS Order No, Advanced Billing Name: Editable for NOT_ALLOCATED, REQUESTED, ALLOCATED, RETAILED
      ctdms_order_no: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      advanced_billing_name: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),

      // Pricing fields: Editable for NOT_ALLOCATED, REQUESTED, ALLOCATED, RETAILED
      ex_showroom_price: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      registration_charges: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      road_tax: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      rto_cost: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      insurance_amount: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      insurance_company: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      handling_charges: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      extended_warranty: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      amc_amount: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      accessories_dlc_amt: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      protection_3m_amount: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      fastag: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      tcs: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      choice_number_charges: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      additional_discount: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),

      // Deal Related Fields: Editable for NOT_ALLOCATED, REQUESTED, ALLOCATED, RETAILED
      other_oem_offer: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      consumer_offer: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      discount_approval_insurance: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      exchange_offer: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      corporate_offer: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
      other_dealer_offer: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),

      // In-house Finance: Editable for NOT_ALLOCATED, REQUESTED, ALLOCATED, RETAILED
      in_house_finance: [
        STOCK_CURRENT_STATUS_NAME.NOT_ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.REQUESTED,
        STOCK_CURRENT_STATUS_NAME.ALLOCATED,
        STOCK_CURRENT_STATUS_NAME.RETAILED,
      ].includes(normalizedStatus),
    };

    // Return the editable status for the field, default to false if not found
    return fieldEditableMap[fieldName] || false;
  };

  const handleModelChange = (value) => {
    setVehicleIds((state) => ({
      ...state,
      model_id: value,
    }));

    form.setFieldsValue({
      vehicle_variant_id: undefined,
      color_id: undefined,
      fuel_type_id: undefined,
      seater_id: undefined,
    });
    setColorData([]);
    setSeatData([]);
    setFuelData([]);
    getVariantData(value);
  };

  const handleVariantChange = (value) => {
    setVehicleIds((state) => ({
      ...state,
      variant_id: value,
    }));
    form.setFieldsValue({
      color_id: undefined,
      fuel_type_id: undefined,
      seater_id: undefined,
    });
    getColorData(vehicleIds?.make_id, vehicleIds?.model_id, value);
    getFuelData(vehicleIds?.make_id, vehicleIds?.model_id, value);
    getSeatData(vehicleIds?.make_id, vehicleIds?.model_id, value);
  };

  const onFinish = async (values) => {
    try {
      setIsLoading(true);

      // Format the date fields to YYYY-MM-DD format
      const formattedWddpDate = values.wddpDate
        ? dayjs(values.wddpDate).format("YYYY-MM-DD")
        : "";
      const formattedMddpMonth = values.mddp_month
        ? dayjs(values.mddp_month).format("YYYY-MM")
        : "";
      const formattedOemInvoiceDate = values.oem_invoice_date
        ? dayjs(values.oem_invoice_date).format("YYYY-MM-DD")
        : null;

      if (isEditMode) {
        // Prepare payload for update
        const payload = {
          id: id || "",
          chassis_number: values.vin || form.getFieldValue("vin") || "",
          vehicle_model_id: values.vehicle_model_id || "",
          vehicle_variant_id: values.vehicle_variant_id || "",
          color_id: values.color_id || "",
          fuel_type_id: values.fuel_type_id || "",
          seater_id: values.seater_id || "",
          stock_status: values.stock_status,
          physical_location_id: values.area_id || "",
          tkm_price: values.tkmPrice || 0,
          wddp_date: formattedWddpDate,
          oem_invoice_date: formattedOemInvoiceDate,
          blocked_status: values.blocked_status || "No",
          blocked_reason: values.blocked_reason || "",
          co_dealer_purchase: values.co_dealer_purchase || "No",
          ctdms_order_no: values.ctdms_order_no || "",
          advanced_billing_name: values.advanced_billing_name || "",
        };

        // Only add mddp_month if stock_status is MDDP
        if (values.stock_status === "MDDP") {
          payload.mddp_month = formattedMddpMonth;
        }

        const response = await updateVinService(payload);

        if (
          response.data.status === API_RESPONSE_CODES.OK ||
          response.data.status === API_RESPONSE_CODES.CREATED
        ) {
          notification.success({
            message: response.data.message || "VIN updated successfully",
          });
          navigate("/admin/stock");
        }
      } else {
        // Prepare payload for add
        const payload = {
          chassis_number: values.vin,
          vehicle_model_id: values.vehicle_model_id,
          vehicle_variant_id: values.vehicle_variant_id,
          color_id: values.color_id,
          fuel_type_id: values.fuel_type_id,
          seater_id: values.seater_id,
          physical_location_id: values.area_id,
          wddp_date: formattedWddpDate,
          oem_invoice_date: formattedOemInvoiceDate,
          tkm_price: parseFloat(values.tkmPrice).toFixed(2),
          stock_status: values.stock_status,
        };

        // Only add mddp_month if stock_status is MDDP
        if (values.stock_status === "MDDP") {
          payload.mddp_month = formattedMddpMonth;
        }

        const response = await addChassisNumberService(payload);

        if (
          response.data.status === API_RESPONSE_CODES.OK ||
          response.data.status === API_RESPONSE_CODES.CREATED
        ) {
          notification.success({
            message:
              response.data.message || "Chassis number added successfully",
          });
          navigate("/admin/stock");
        }
      }
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="user-data">
      <Loader loading={isLoading} />
      <PageContentHeader
        title={isEditMode ? "Edit Chassis Form" : "Add Chassis Form"}
        headerRight={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/admin/stock")}
            type="text"
          >
            Back
          </Button>
        }
      />
      <div className="personal-detail">
        <Title level={4} className="pe-2 pb-2"></Title>
        <Card>
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            initialValues={{
              vin,
              wddpDate: dayjs(),
              tkmPrice: 0,
            }}
          >
            <Row gutter={16}>
              <Col xs={24} md={24} lg={8}>
                <Form.Item
                  name="vin"
                  label="Chassis Number"
                  normalize={sanitizeChassisInput}
                  rules={[
                    { required: true, message: "Please enter chassis number" },
                    {
                      len: 17,
                      message: "Chassis number must be exactly 17 characters",
                    },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        if (!/^[A-Za-z0-9]+$/.test(value)) {
                          return Promise.reject(
                            "Chassis number must be numeric or alphanumeric only (special characters are not allowed)"
                          );
                        }
                        if (!/\d/.test(value)) {
                          return Promise.reject(
                            "Chassis number cannot be letters only; it must include at least one number"
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    className="form-control-ant"
                    placeholder="Enter 17 character numeric or alphanumeric chassis number"
                    disabled={!isChassisEditable}
                    maxLength={17}
                    onKeyDown={blockInvalidChassisKeys}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <InputSelect
                  className="form-control-ant"
                  name="stock_status"
                  label="Stock Status"
                  rules={[
                    {
                      required: isEditMode
                        ? isFieldEnabled("stock_status")
                        : true,
                      message: "Please select stock status",
                    },
                  ]}
                  options={masterOptions.stockStatus.map((v) => ({
                    id: v,
                    name: v,
                  }))}
                  optionName="name"
                  placeholder="Select Stock Status"
                  disabled={isEditMode && !isFieldEnabled("stock_status")}
                  onSelect={(value) => {
                    // Clear MDDP Month when stock status is not MDDP (both add and edit modes)
                    if (value !== "MDDP") {
                      form.setFieldsValue({ mddp_month: null });
                    }
                  }}
                />
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item
                  name="vehicle_model_id"
                  label="Model"
                  rules={[
                    {
                      required: isEditMode
                        ? hasLeadId
                          ? false
                          : isFieldEnabled("vehicle_model_id")
                        : true,
                      message: "Please select model",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Model"
                    onChange={handleModelChange}
                    className="form-control-ant"
                    disabled={
                      isEditMode &&
                      (hasLeadId || !isFieldEnabled("vehicle_model_id"))
                    }
                  >
                    {modalData?.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option?.model_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item
                  name="vehicle_variant_id"
                  label="Variant"
                  rules={[
                    {
                      required: isEditMode
                        ? hasLeadId
                          ? false
                          : isFieldEnabled("vehicle_variant_id")
                        : true,
                      message: "Please select variant",
                    },
                  ]}
                >
                  <Select
                    onChange={handleVariantChange}
                    className="form-control-ant"
                    placeholder="Select Variant"
                    disabled={
                      isEditMode &&
                      (hasLeadId || !isFieldEnabled("vehicle_variant_id"))
                    }
                  >
                    {variantData?.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option?.variant_code}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item
                  name="color_id"
                  label="Color"
                  rules={[
                    {
                      required: isEditMode
                        ? hasLeadId
                          ? false
                          : isFieldEnabled("color_id")
                        : true,
                      message: "Please select Color",
                    },
                  ]}
                >
                  <Select
                    className="form-control-ant"
                    placeholder="Select Color"
                    disabled={
                      isEditMode && (hasLeadId || !isFieldEnabled("color_id"))
                    }
                  >
                    {colorData?.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option?.color}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item
                  name="fuel_type_id"
                  label="Fuel Type"
                  rules={[
                    {
                      required: isEditMode
                        ? hasLeadId
                          ? false
                          : isFieldEnabled("fuel_type_id")
                        : true,
                      message: "Please select fuel type",
                    },
                  ]}
                >
                  <Select
                    className="form-control-ant"
                    placeholder="Select Fuel Type"
                    disabled={
                      isEditMode &&
                      (hasLeadId || !isFieldEnabled("fuel_type_id"))
                    }
                  >
                    {fuelData?.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option?.title}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item
                  name="seater_id"
                  label="Seater"
                  rules={[
                    {
                      required: isEditMode
                        ? hasLeadId
                          ? false
                          : isFieldEnabled("seater_id")
                        : true,
                      message: "Please select seater",
                    },
                  ]}
                >
                  <Select
                    className="form-control-ant"
                    placeholder="Select Seater"
                    disabled={
                      isEditMode && (hasLeadId || !isFieldEnabled("seater_id"))
                    }
                  >
                    {seatData?.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option?.seater}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item
                  name="tkmPrice"
                  label="TKM Price"
                  normalize={normalizeDecimalInput}
                  rules={[
                    {
                      required: isEditMode ? isFieldEnabled("tkmPrice") : true,
                      message: "Please enter TKM price",
                    },
                    {
                      pattern: /^\d+(\.\d{1,2})?$/,
                      message: "Maximum 2 decimal places allowed",
                    },
                    {
                      validator: (_, value) => {
                        if (!value && value !== 0) {
                          return Promise.resolve();
                        }
                        const numValue = parseFloat(value);
                        if (numValue < 100000) {
                          return Promise.reject(
                            "Price must be at least ₹1,00,000"
                          );
                        }
                        if (numValue > 10000000) {
                          return Promise.reject(
                            "Price must not exceed ₹1,00,00,000"
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    className="form-control-ant"
                    placeholder="Enter Price (₹1,00,000 - ₹1,00,00,000)"
                    type="number"
                    disabled={isEditMode && !isFieldEnabled("tkmPrice")}
                    onKeyDown={blockInvalidNumberKeys}
                    step="0.01"
                  />
                </Form.Item>
              </Col>
              {stockStatus !== "MDDP" && (
                <Col xs={24} md={24} lg={8}>
                  <Form.Item
                    name="oem_invoice_date"
                    label="OEM Invoice Date"
                    required={
                      stockStatus === "Physical" &&
                      (!isEditMode || isFieldEnabled("oem_invoice_date"))
                    }
                    dependencies={["stock_status"]}
                    rules={[
                      {
                        validator: (_, value) => {
                          const currentStockStatus =
                            form.getFieldValue("stock_status");
                          if (currentStockStatus !== "Physical") {
                            return Promise.resolve();
                          }
                          if (isEditMode) {
                            if (
                              isFieldEnabled("oem_invoice_date") &&
                              !value
                            ) {
                              return Promise.reject(
                                "Please select OEM Invoice Date."
                              );
                            }
                            return Promise.resolve();
                          }
                          if (!value) {
                            return Promise.reject(
                              "Please select OEM Invoice Date."
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <DatePicker
                      className="form-control-ant"
                      placeholder="Select a Date"
                      style={{ width: "100%" }}
                      format="DD/MM/YYYY"
                      disabled={
                        isEditMode &&
                        (stockStatus !== "Physical" ||
                          !isFieldEnabled("oem_invoice_date"))
                      }
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} md={24} lg={8}>
                <Form.Item
                  name="wddpDate"
                  label="WDDP Date"
                  dependencies={["stock_status", "mddp_month"]}
                  rules={[
                    {
                      required: isEditMode ? isFieldEnabled("wddpDate") : true,
                      message: "Please select WDDP date",
                    },
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }
                        // Only validate when stock status is MDDP
                        const currentStockStatus = form.getFieldValue("stock_status");
                        if (currentStockStatus === "MDDP") {
                          const mddpMonth = form.getFieldValue("mddp_month");
                          if (mddpMonth) {
                            const wddpMonth = dayjs(value).startOf("month");
                            const mddpMonthStart = dayjs(mddpMonth).startOf("month");
                            // WDDP Date must be in the same month or after MDDP Month
                            if (wddpMonth.isBefore(mddpMonthStart, "month")) {
                              return Promise.reject(
                                "WDDP Date must be in the same month or after MDDP Month"
                              );
                            }
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <DatePicker
                    className="form-control-ant"
                    placeholder="Select a Date"
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    disabled={isEditMode && !isFieldEnabled("wddpDate")}
                    disabledDate={(current) => {
                      if (!current) return false;
                      const today = dayjs().startOf("day");
                      const past180Days = today.subtract(180, "days");
                      const future180Days = today.add(180, "days");
                      return (
                        current.isBefore(past180Days, "day") ||
                        current.isAfter(future180Days, "day")
                      );
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Form.Item
                  name="area_id"
                  label="Physical Location"
                  rules={[
                    {
                      required: isEditMode ? isFieldEnabled("area_id") : true,
                      message: "Please select physical location",
                    },
                  ]}
                >
                  <Select
                    className="form-control-ant"
                    placeholder="Select Physical Location"
                    disabled={isEditMode && !isFieldEnabled("area_id")}
                  >
                    {areaData?.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option?.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* MDDP Month - Visible only when Stock Status is MDDP in both Add and Edit modes */}
              {stockStatus === "MDDP" && (
                <Col xs={24} md={24} lg={8}>
                  <Form.Item
                    name="mddp_month"
                    label="MDDP Month"
                    rules={[
                      {
                        required: isEditMode ? isFieldEnabled("mddp_month") : true,
                        message: "Please select MDDP month",
                      },
                    ]}
                  >
                    <DatePicker
                      className="form-control-ant"
                      placeholder="Select MDDP Month"
                      style={{ width: "100%" }}
                      format="MMM-YYYY"
                      picker="month"
                      disabled={isEditMode && !isFieldEnabled("mddp_month")}
                      disabledDate={(current) => {
                        if (!current) return false;
                        const today = dayjs().startOf("month");
                        const past180Days = today.subtract(180, "days");
                        const future180Days = today.add(180, "days");
                        return (
                          current.isBefore(past180Days, "month") ||
                          current.isAfter(future180Days, "month")
                        );
                      }}
                      onChange={() => {
                        // Trigger validation for WDDP date when MDDP month changes (only if stock status is MDDP)
                        const currentStockStatus = form.getFieldValue("stock_status");
                        if (currentStockStatus === "MDDP") {
                          setTimeout(() => {
                            form.validateFields(["wddpDate"]);
                          }, 100);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              )}

              {/* Additional fields for Edit mode only */}
              {isEditMode && (
                <>
                  {/* Blocking Section - Only in Edit mode */}
                  <Col xs={24} md={24} lg={8}>
                    <Form.Item
                      name="blocked_status"
                      label="Blocked Status"
                      rules={[
                        {
                          required: isFieldEnabled("blocked_status"),
                          message: "Please select blocked status",
                        },
                      ]}
                    >
                      <Select
                        className="form-control-ant"
                        placeholder="Select Blocked Status"
                        disabled={!isFieldEnabled("blocked_status")}
                        onChange={(value) => {
                          if (value === "No") {
                            form.setFieldsValue({ blocked_reason: null });
                            // Clear validation errors for blocked_reason
                            form.setFields([
                              { name: "blocked_reason", errors: [] },
                            ]);
                          }
                        }}
                      >
                        <Select.Option value="No">No</Select.Option>
                        <Select.Option value="Yes">Yes</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={24} lg={8}>
                    <Form.Item
                      name="blocked_reason"
                      label="Blocked Reason"
                      dependencies={["blocked_status"]}
                      validateTrigger={["onChange", "onBlur"]}
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.blocked_status !==
                        currentValues.blocked_status
                      }
                      rules={[
                        {
                          validator: (_, value) => {
                            // Get the latest blocked_status value
                            const blockedStatus =
                              form.getFieldValue("blocked_status");
                            // Only validate if blocked_status is 'Yes' and field is enabled
                            if (
                              blockedStatus === "Yes" &&
                              isFieldEnabled("blocked_reason", blockedStatus)
                            ) {
                              // Check if value exists - allow any non-empty value
                              if (
                                value === undefined ||
                                value === null ||
                                value === ""
                              ) {
                                return Promise.reject(
                                  "Please select blocked reason"
                                );
                              }
                            }
                            // If blocked_status is not 'Yes' or field is disabled, validation passes
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <BlockedReasonSelectWrapper
                        form={form}
                        isFieldEnabled={isFieldEnabled}
                      />
                    </Form.Item>
                  </Col>

                  {/* Order Details Section - Only in Edit mode */}
                  <Col xs={24} md={24} lg={8}>
                    <Form.Item
                      name="ctdms_order_no"
                      label="CTDMS Order No."
                      rules={[
                        {
                          required: false,
                          message: "",
                        },
                        {
                          max: 12,
                          message:
                            "CTDMS Order No. must be maximum 12 characters",
                        },
                        {
                          pattern: /^[A-Za-z0-9]+$/,
                          message: "CTDMS Order No. must be alphanumeric only",
                        },
                      ]}
                    >
                      <Input
                        className="form-control-ant"
                        placeholder="Enter CTDMS Order No. (12 characters)"
                        maxLength={12}
                        disabled={!isFieldEnabled("ctdms_order_no")}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={24} lg={8}>
                    <Form.Item
                      name="advanced_billing_name"
                      label="Advanced Billing Name"
                      rules={[
                        {
                          required: false,
                          message: "",
                        },
                        {
                          validator: (_, value) => {
                            if (!value) {
                              return Promise.resolve();
                            }
                            // Check both conditions together
                            const isValidLength =
                              value.length >= 5 && value.length <= 50;
                            const isValidPattern = /^[A-Za-z\s]+$/.test(value);

                            if (!isValidLength || !isValidPattern) {
                              return Promise.reject(
                                "Advanced Billing Name must be between 5 to 50 characters and contain only letters and spaces"
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        className="form-control-ant"
                        placeholder="Enter Advanced Billing Name (5-50 characters)"
                        maxLength={50}
                        disabled={!isFieldEnabled("advanced_billing_name")}
                      />
                    </Form.Item>
                  </Col>

                  {/* Co-dealer Purchase - Only in Edit mode */}
                  <Col xs={24} md={24} lg={8}>
                    <Form.Item
                      name="co_dealer_purchase"
                      label="Co-dealer Purchase"
                      rules={[
                        {
                          required: isFieldEnabled("co_dealer_purchase"),
                          message: "Please select co-dealer purchase",
                        },
                      ]}
                    >
                      <Select
                        className="form-control-ant"
                        placeholder="Select Co-dealer Purchase"
                        disabled={!isFieldEnabled("co_dealer_purchase")}
                      >
                        <Select.Option value="Yes">Yes</Select.Option>
                        <Select.Option value="No">No</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>

            <Row gutter={8} className="mt-3">
              <Col>
                <Button
                  type="primary"
                  ghost
                  danger
                  className="primary-button"
                  onClick={() => navigate("/admin/stock")}
                >
                  Cancel
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="secondary-button"
                >
                  Save
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default AddEditVinForm;
